import React, { useCallback, useEffect, useState } from "react";
import style from "css/hotdesking.module.css";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { ReactComponent as ReloadIcon } from "asset/hotdesking/reload.svg";
import { ReactComponent as SpotIcon } from "asset/hotdesking/spot.svg";
import { ReactComponent as CheckInIcon } from "asset/hotdesking/checkin.svg";
import { ReactComponent as CheckOutIcon } from "asset/hotdesking/checkout.svg";
import { ReactComponent as SeatIcon } from "asset/hotdesking/seat.svg";
import { ReactComponent as ShareIcon } from "asset/hotdesking/share.svg";
import { useSelector } from "react-redux";
import { Button, ConfigProvider } from "antd";
import axios from "axios";
import HotdeskingConfig from "../Config";
import Loading from "components/common/Loading";
import { useTranslation } from "react-i18next";

const type = {
  available: "AVAILABLE",
  in_use: "IN_USE",
  not_use: "NOT_USE",
  error: "ERROR",
};

function TodayMenu({
  data,
  date,
  time,
  onCancel,
  onCheckIn,
  onCheckOut,
  onExtend,
  onShare,
  onShowBook,
  onShowResource,
  onBook,
  onError,
}) {
  const { i18n } = useTranslation();
  dayjs.locale(i18n.language);
  return (
    <section className={style.menu_today}>
      <ScheduleContent
        data={data}
        date={date}
        time={time}
        onCancel={onCancel}
        onCheckIn={onCheckIn}
        onCheckOut={onCheckOut}
        onExtend={onExtend}
        onShare={onShare}
        onShowBook={onShowBook}
        onShowResource={onShowResource}
        onBook={onBook}
        onError={onError}
      />
    </section>
  );
}

export default TodayMenu;

function ScheduleContent({
  data,
  date,
  time,
  onCancel,
  onCheckIn,
  onCheckOut,
  onExtend,
  onShare,
  onShowBook,
  onShowResource,
  onBook,
  onError,
}) {
  const user = useSelector((state) => state.user);
  const contentRender = () => {
    if (data !== null) {
      switch (data.status) {
        case type.available:
          return (
            <Available
              user={user}
              data={data}
              onCheckIn={onCheckIn}
              onCancel={onCancel}
              onShare={onShare}
              onShowBook={onShowBook}
            />
          );
        case type.in_use:
          return (
            <InUse
              user={user}
              data={data}
              onCheckOut={onCheckOut}
              onExtend={onExtend}
              onShare={onShare}
              onShowBook={onShowBook}
            />
          );
        case type.not_use:
          return (
            <NotUse
              user={user}
              date={date}
              time={time}
              onBook={onBook}
              onShowResource={onShowResource}
              onError={onError}
            />
          );
        default:
          return <></>;
      }
    } else {
      return (
        <NotUse
          user={user}
          date={date}
          time={time}
          onBook={onBook}
          onShowResource={onShowResource}
          onError={onError}
        />
      );
    }
  };
  return <section className={style.today_content}>{contentRender()}</section>;
}
const formatDate = (date, language) => {
  if (language === "ko-KR") {
    return dayjs(new Date(date)).format("YYYY년 MM월 DD일 (ddd요일)");
  } else {
    return dayjs(new Date(date)).format("YYYY. MM. DD. (ddd)");
  }
};
const formatTime = (startDttm, endDttm) => {
  const formatStartDttm = dayjs(new Date(startDttm));
  const formatEndDttm = dayjs(new Date(endDttm));
  return (
    <>
      {formatStartDttm.format("HH:mm")} ~ {formatEndDttm.format("HH:mm")}
    </>
  );
};
const formatDuration = (duration, language) => {
  let hours = Math.floor(duration / 60);
  let mins = duration % 60;

  if (language === "ko-KR") {
    return `${String(hours).padStart(2, "0")}시간 ${String(mins).padStart(
      2,
      "0"
    )}분`;
  } else {
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  }
};
function Available({ user, data, onCheckIn, onCancel, onShare, onShowBook }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <section className={style.content_section}>
        <section className={style.content_background}>
          <picture>
            <source
              className={style.background_image}
              srcSet={
                process.env.REACT_APP_CLIENT_URL + "img/hotdesking/checkin.webp"
              }
              type="image/webp"
            />
            <img
              className={style.background_image}
              src={
                process.env.REACT_APP_CLIENT_URL + "img/hotdesking/checkin.png"
              }
              alt="checkin"
            />
          </picture>
          <picture>
            <source
              className={style.background_image}
              srcSet={
                process.env.REACT_APP_CLIENT_URL +
                "img/hotdesking/checkin_bottom.webp"
              }
              type="image/webp"
            />
            <img
              className={style.background_image}
              src={
                process.env.REACT_APP_CLIENT_URL +
                "img/hotdesking/checkin_bottom.png"
              }
              alt="checkin"
            />
          </picture>
        </section>

        <section className={style.content_article_container}>
          <article className={`${style.content_article} ${style.available}`}>
            <div className={style.banner}>
              {formatDate(data.startDttm, i18n.language)}
            </div>

            <div className={style.comment}>
              <p>{t("HOTDESKING.MENU.AVAILABLE.TITLE", { NAME: user.name })}</p>
            </div>

            <p className={style.department}>
              {data.resource.location.departmentTitle}
            </p>

            <div className={style.location}>
              <div className={style.item}>
                <p>{data.resource.location.title}</p>
                <p>Location</p>
              </div>

              <span className={style.divider} />

              <div className={style.item}>
                <p>{data.resource.title}</p>
                <p>No.</p>
              </div>
            </div>

            <article className={style.duration}>
              <p>{formatTime(data.startDttm, data.endDttm)}</p>
              <p>({formatDuration(data.duration, i18n.language)})</p>
            </article>

            <div className={style.ticket}>
              <ConfigProvider
                theme={{
                  components: {
                    Button: {
                      defaultColor: "#fff",
                      defaultBorderColor: "#1cb691",
                      defaultBg: "#1cb691",
                      defaultHoverColor: "#fff",
                      defaultHoverBorderColor: "#1cb691",
                      defaultHoverBg: "#1cb591cc",
                      defaultActiveColor: "#fff",
                      defaultActiveBorderColor: "#1cb691",
                      defaultActiveBg: "#1cb691",
                    },
                  },
                }}
              >
                <Button className={style.button} onClick={onCheckIn}>
                  <CheckInIcon />
                  <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.CHECK_IN")}</p>
                </Button>
              </ConfigProvider>
            </div>
          </article>

          <section className={style.options}>
            <div className={style.item} onClick={() => onShowBook(data)}>
              <SeatIcon />
              <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.MY_RESERVATION")}</p>
            </div>

            <span className={style.divider} />

            <div className={style.item} onClick={onShare}>
              <ShareIcon />
              <p>{t("HOTDESKING.MENU.AVAILABLE.BUTTON.SHARE")}</p>
            </div>
          </section>
        </section>
      </section>

      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultColor: "#1cb691",
              defaultBorderColor: "#1cb691",
              defaultBg: "#fff",
              defaultHoverColor: "#fff",
              defaultHoverBorderColor: "#1cb691",
              defaultHoverBg: "#1cb591",
              defaultActiveColor: "#fff",
              defaultActiveBorderColor: "#1cb691",
              defaultActiveBg: "#1cb591cc",
            },
          },
        }}
      >
        <Button className={style.content_button} onClick={() => onCancel(data)}>
          {t("HOTDESKING.MENU.AVAILABLE.BUTTON.CANCEL")}
        </Button>
      </ConfigProvider>
    </>
  );
}
function InUse({ user, data, onCheckOut, onExtend, onShare, onShowBook }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <section className={style.content_section}>
        <section className={style.content_background}>
          <picture>
            <source
              className={style.background_image}
              srcSet={
                process.env.REACT_APP_CLIENT_URL +
                "img/hotdesking/checkout.webp"
              }
              type="image/webp"
            />
            <img
              className={style.background_image}
              src={
                process.env.REACT_APP_CLIENT_URL + "img/hotdesking/checkout.png"
              }
              alt="checkout"
            />
          </picture>

          <picture>
            <source
              className={style.background_image}
              srcSet={
                process.env.REACT_APP_CLIENT_URL +
                "img/hotdesking/checkout_bottom.webp"
              }
              type="image/webp"
            />
            <img
              className={style.background_image}
              src={
                process.env.REACT_APP_CLIENT_URL +
                "img/hotdesking/checkout_bottom.png"
              }
              alt="checkout"
            />
          </picture>
        </section>

        <section className={style.content_article_container}>
          <article className={`${style.content_article} ${style.in_use}`}>
            <div className={style.banner}>
              {formatDate(data.startDttm, i18n.language)}
            </div>

            <div className={style.comment}>
              <p>{t("HOTDESKING.MENU.IN_USE.TITLE", { NAME: user.name })}</p>
            </div>

            <p className={style.department}>
              {data.resource.location.departmentTitle}
            </p>

            <div className={style.location}>
              <div className={style.item}>
                <p>{data.resource.location.title}</p>
                <p>Location</p>
              </div>

              <span className={style.divider} />

              <div className={style.item}>
                <p>{data.resource.title}</p>
                <p>No.</p>
              </div>
            </div>

            <article className={style.duration}>
              <p>{formatTime(data.startDttm, data.endDttm)}</p>
              <p>({formatDuration(data.duration, i18n.language)})</p>
            </article>

            <div className={style.ticket}>
              <p className={style.ticket_comment}>
                {t("HOTDESKING.MENU.IN_USE.BUTTON.CHECK_OUT")}
              </p>
              <ConfigProvider
                theme={{
                  components: {
                    Button: {
                      defaultColor: "#fff",
                      defaultBorderColor: "#1cb691",
                      defaultBg: "#1cb691",
                      defaultHoverColor: "#fff",
                      defaultHoverBorderColor: "#1cb691",
                      defaultHoverBg: "#1cb591cc",
                      defaultActiveColor: "#fff",
                      defaultActiveBorderColor: "#1cb691",
                      defaultActiveBg: "#1cb691",
                    },
                  },
                }}
              >
                <Button
                  className={style.button}
                  onClick={() => onCheckOut(data)}
                >
                  <div className={style.button_content}>
                    <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.CHECK_OUT")}</p>
                    <CheckOutIcon />
                  </div>
                </Button>
              </ConfigProvider>
            </div>
          </article>

          <section className={style.options}>
            <div className={style.item} onClick={() => onShowBook(data)}>
              <SeatIcon />
              <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.MY_RESERVATION")}</p>
            </div>

            <span className={style.divider} />

            <div className={style.item} onClick={onShare}>
              <ShareIcon />
              <p>{t("HOTDESKING.MENU.IN_USE.BUTTON.SHARE")}</p>
            </div>
          </section>
        </section>
      </section>

      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultColor: "#fff",
              defaultBorderColor: "#1cb691",
              defaultBg: "#1cb691",
              defaultHoverColor: "#fff",
              defaultHoverBorderColor: "#1cb691",
              defaultHoverBg: "#1cb591cc",
              defaultActiveColor: "#fff",
              defaultActiveBorderColor: "#1cb691",
              defaultActiveBg: "#1cb691",
            },
          },
        }}
      >
        <Button className={style.content_button} onClick={() => onExtend(data)}>
          {t("HOTDESKING.MENU.IN_USE.BUTTON.EXTEND")}
        </Button>
      </ConfigProvider>
    </>
  );
}
function NotUse({ user, date, time, onBook, onShowResource, onError }) {
  const { t } = useTranslation();
  const [isRefresh, setIsRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [resource, setResource] = useState(null);

  const getResource = useCallback(() => {
    setIsLoading(true);
    const config = {
      params: {
        start_dttm: date + " " + time.start,
        end_dttm: date + " " + time.end,
      },
    };
    axios
      .get(`${HotdeskingConfig.baseURI}/book/resource`, config)
      .then((res) => {
        setResource(res.data);
      })
      .catch((err) => {
        if (err === undefined || err.response === undefined) {
          onError(t("HOTDESKING.MENU.NOT_USE.MESSAGE.CANNOT_FIND_SEAT"));
        } else {
          onError(err.response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date, onError, t, time.end, time.start]);

  useEffect(() => {
    if (isRefresh) {
      getResource();
      setIsRefresh(false);
    }
  }, [getResource, isRefresh]);

  const onRefresh = () => {
    setIsRefresh(true);
  };

  return (
    <>
      <section className={style.content_section}>
        <section className={style.content_background}>
          <picture>
            <source
              className={style.background_image}
              srcSet={
                process.env.REACT_APP_CLIENT_URL + "img/hotdesking/suggest.webp"
              }
              type="image/webp"
            />
            <img
              className={style.background_image}
              src={
                process.env.REACT_APP_CLIENT_URL + "img/hotdesking/suggest.png"
              }
              alt="suggest"
            />
          </picture>
        </section>

        <section className={style.content_article_container}>
          <article className={`${style.content_article} ${style.not_use}`}>
            <div className={style.banner}>
              {t("HOTDESKING.MENU.NOT_USE.TITLE")}
            </div>
            <div className={style.comment}>
              <p>
                {t("HOTDESKING.MENU.NOT_USE.DESC.DESC_1", { NAME: user.name })}
              </p>
              <p>{t("HOTDESKING.MENU.NOT_USE.DESC.DESC_2")}</p>
            </div>

            {isLoading ? (
              <Loading />
            ) : (
              <p className={style.department}>
                {resource?.location.departmentTitle}
              </p>
            )}
            <div className={style.location}>
              <div className={style.item}>
                {isLoading ? (
                  <Loading />
                ) : (
                  <>
                    <p>{resource?.location.title}</p>
                    <p>Location</p>
                  </>
                )}
              </div>
              <span className={style.divider} />
              <div className={style.item}>
                {isLoading ? (
                  <Loading />
                ) : (
                  <>
                    <p>{resource?.title}</p>
                    <p>No.</p>
                  </>
                )}
              </div>
            </div>
            <div className={style.article_options}>
              {/* <div className={style.item}>
                      <SilentIcon />
                      <p>조용한 공간</p>
                    </div>

                    <div className={style.item}>
                      <AlramIcon />
                      <p>방해받지 않는 자리</p>
                    </div> */}
            </div>
          </article>

          <section className={style.options}>
            <div
              className={style.item}
              onClick={() => onShowResource(resource)}
            >
              <SpotIcon />
              <p>{t("HOTDESKING.MENU.NOT_USE.BUTTON.VIEW_SEAT")}</p>
            </div>

            <span className={style.divider} />

            <div className={style.item} onClick={onRefresh}>
              <ReloadIcon />
              <p>
                {t("HOTDESKING.MENU.NOT_USE.BUTTON.REQUEST_RECOMMENDATION")}
              </p>
            </div>
          </section>
        </section>
      </section>

      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultColor: "#fff",
              defaultBorderColor: "#1cb691",
              defaultBg: "#1cb691",
              defaultHoverColor: "#fff",
              defaultHoverBorderColor: "#1cb691",
              defaultHoverBg: "#1cb591cc",
              defaultActiveColor: "#fff",
              defaultActiveBorderColor: "#1cb691",
              defaultActiveBg: "#1cb691",
            },
          },
        }}
      >
        <Button
          className={style.content_button}
          onClick={() => onBook(resource)}
        >
          {t("HOTDESKING.MENU.NOT_USE.BUTTON.RESERVATION")}
        </Button>
      </ConfigProvider>
    </>
  );
}
